import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useMediaQuery, useTheme } from '@mui/material';
import FormatTime from 'components/FormatTime';
import DownloadDialog from 'components/Modals/implementations/DownloadDialog';
import {
  DesktopTrackActionButtons,
  MobileTrackActionButtons,
  PlayButton,
} from 'pages/AIMusicGenerator/components/Track/components/TrackContent/components';
import { makeAddToLibraryHandler } from 'pages/AIMusicGenerator/components/Track/components/TrackContent/helpers';
import useTrack from 'pages/AIMusicGenerator/components/Track/useTrack';
import { events } from 'services/analytics';
import { AnalyticsEventLocation } from 'services/analytics/entities';
import { useAuthStore, useEmailConfirmationDialog } from 'services/hooks';
import useLibraryTrack from 'services/hooks/aiMusicGenerator';
import useAnalytics from 'services/hooks/analytics/useAnalytics';
import useTrackAISongDownload from 'services/hooks/analytics/useTrackAISongDownload';
import { type VegaSong } from 'services/store/ai/types';
import { selectEmailConfirmed } from 'services/store/user/selectors';
import { TrackDuration, TrackProgress, TrackTitle } from '../TrackGrid';
import { StyledSlider, StyledTrackGrid } from './styled';

const location = AnalyticsEventLocation.AI_MUSIC_GENERATOR;

interface TrackContentProps {
  song: VegaSong;
}

const TrackContent = ({ song }: TrackContentProps) => {
  const theme = useTheme();
  const { authStore } = useAuthStore();
  const { track } = useAnalytics();

  const { title, id, duration, songType, genre } = song;

  const { isPlaying, play, progress, pause, seek } = useTrack(song);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const { isInLibrary, addToLibrary, removeFromLibrary } =
    useLibraryTrack(song);
  const { isLoggedIn } = authStore;

  const isMobile = useMediaQuery(theme.breakpoints.down('desktop'));

  const isEmailConfirmed = useSelector(selectEmailConfirmed);
  const {
    onEmailConfirmationDialogShouldOpen,
    displayEmailConfirmationDialog,
  } = useEmailConfirmationDialog();

  const shouldDisplayEmailConfirmationDialog = useMemo(() => {
    if (!isLoggedIn) {
      return false;
    }
    return !isEmailConfirmed;
  }, [isLoggedIn, isEmailConfirmed]);

  const playHandler = () => {
    play();
    track(events.playAIGeneratedSong(title, location));
  };

  const addToLibraryHandler = makeAddToLibraryHandler(
    shouldDisplayEmailConfirmationDialog,
    onEmailConfirmationDialogShouldOpen,
    isInLibrary,
    addToLibrary
  );

  const downloadHandler = () => {
    if (shouldDisplayEmailConfirmationDialog) {
      onEmailConfirmationDialogShouldOpen();
      return;
    }
    setIsDownloadModalOpen(true);
  };

  const { trackDownload } = useTrackAISongDownload(
    genre,
    title,
    songType,
    location
  );

  const onModalClose = () => setIsDownloadModalOpen(false);

  return (
    <StyledTrackGrid>
      <PlayButton isPlaying={isPlaying} play={playHandler} pause={pause} />
      <TrackTitle>{title}</TrackTitle>
      <TrackDuration>
        <FormatTime time={duration / 1000} />
      </TrackDuration>
      <TrackProgress
        sx={{
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <StyledSlider
          size="small"
          min={0}
          max={1}
          step={0.01}
          value={progress}
          // @ts-ignore
          onChange={(_e, v) => seek(v)}
        />
      </TrackProgress>
      {isMobile ? (
        <MobileTrackActionButtons
          song={song}
          removeFromLibrary={removeFromLibrary}
          addToLibraryHandler={addToLibraryHandler}
          location={location}
          downloadHandler={downloadHandler}
        />
      ) : (
        <DesktopTrackActionButtons
          song={song}
          addToLibraryHandler={addToLibraryHandler}
          removeFromLibrary={removeFromLibrary}
          location={location}
          isInLibrary={isInLibrary}
        />
      )}
      {displayEmailConfirmationDialog()}
      <DownloadDialog
        songId={id}
        songType={songType}
        isOpen={isDownloadModalOpen}
        onClose={onModalClose}
        addToLibrary={addToLibrary}
        analyticsTrackDownload={trackDownload}
      />
    </StyledTrackGrid>
  );
};

export default TrackContent;
